import { useQueryClient } from '@tanstack/react-query';
import Cookie from 'js-cookie';
import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'shared-routing';

import { Routes } from '@common/constants';
import { parseSpaEntityData } from '@common/components/SpaEntity/SpaEntityContainer';
import { createBasket, updateBasket } from '@store/basket/actions';
import { basketSelectors } from '@store/basket/selectors';
import { SpaEntityType } from 'shared-types/Spa';

import { FacilityFormValues } from '../FacilityBookingV2.types';

type UseSubmitFacilityParams = {
  form: UseFormReturn<FacilityFormValues>;
  spaEntity: SpaEntityType | undefined;
  propertyId: number;
  language: string;
  id: number;
  onClose: (showMiniBasket: boolean) => void;
  hasExtras: boolean;
};

export function useSubmitFacility({
  form,
  spaEntity,
  propertyId,
  language,
  id,
  onClose,
}: UseSubmitFacilityParams) {
  const dispatch = useDispatch();
  const router = useRouter();
  const queryClient = useQueryClient();
  const currentBasketItems = useSelector(
    (state: Record<string, unknown>) =>
      (basketSelectors.getBasketDetails(state) as { items?: unknown[] })?.items || []
  );

  const addToBasket = useCallback(
    (data: FacilityFormValues) => {
      if (!spaEntity) return;
      const parsedEntity = parseSpaEntityData(data as any, spaEntity, 'spa-facilities');
      parsedEntity.guestDetails = parsedEntity.guestDetails.map((guest: any, i: number) => {
        const formGuest = data.guests[i];
        if (formGuest?.loggedInUser) {
          const { id: _id, ...rest } = guest;
          return { ...rest, loggedInUser: formGuest.loggedInUser };
        }
        return guest;
      });
      const basketId = Cookie.get('basketId');

      const promise = basketId
        ? dispatch(
            updateBasket(
              propertyId,
              basketId,
              language,
              {
                items: [...currentBasketItems, parsedEntity],
              },
              false,
              true
            ) as any
          )
        : dispatch(
            createBasket(
              propertyId,
              language,
              {
                items: [parsedEntity],
              },
              true
            ) as any
          );

      Promise.resolve(promise)
        .then(() => {
          queryClient.invalidateQueries({
            predicate: (query) =>
              query.queryKey.includes('spaFacility') && query.queryKey.includes('timeslots'),
          });
          onClose(true);
        })
        .catch(() => {
          router.push({ pathname: Routes.FACILITY_DETAIL, query: { id } });
        });
    },
    [
      spaEntity,
      currentBasketItems,
      dispatch,
      propertyId,
      language,
      queryClient,
      onClose,
      router,
      id,
    ]
  );

  const handleSubmit = form.handleSubmit(addToBasket as any);

  const handleNextStep = form.handleSubmit(() => {
    router.push({
      pathname: Routes.FACILITY_BOOKING,
      query: { id, bookingStep: 'extras' },
    });
  });

  const handleBack = useCallback(() => {
    router.push({ pathname: Routes.FACILITY_DETAIL, query: { id } });
  }, [router, id]);

  return {
    handleSubmit,
    handleNextStep,
    handleBack,
  };
}
